import './PricingSection.css';

import { useEffect, useRef, useState } from 'react';
import { captureProductEvent, markSignupStarted, updateProductAnalyticsContext } from '../utils/productAnalytics';

const PricingSection = ({ isAuthenticated, authLoading = false, onLogin }) => {
  const [billingCycle, setBillingCycle] = useState('monthly'); // Default to monthly
  const [isLoading, setIsLoading] = useState(false);
  const [userPricing, setUserPricing] = useState(null);
  const [fetchingPricing, setFetchingPricing] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const capturedPricingViewRef = useRef(false);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (capturedPricingViewRef.current) return;

    capturedPricingViewRef.current = true;
    captureProductEvent('pricing-viewed', {
      isAuthenticated,
      billingCycle,
    });
  }, [authLoading, billingCycle, isAuthenticated]);

  // Fetch user pricing information when authenticated
  useEffect(() => {
    const fetchUserPricing = async () => {
      if (!isAuthenticated) return;

      setFetchingPricing(true);
      try {
        const response = await fetch('/api/pricing/user', {
          credentials: 'include',
        });

        if (response.ok) {
          const pricingData = await response.json();
          setUserPricing(pricingData);
        } else {
          console.error('Failed to fetch user pricing:', response.statusText);
        }
      } catch (error) {
        console.error('Error fetching user pricing:', error);
      } finally {
        setFetchingPricing(false);
      }
    };

    fetchUserPricing();
  }, [isAuthenticated]);

  const handlePlanSelect = async plan => {
    const interval = plan === 'oneTime' ? 'one_time' : billingCycle;

    if (!isAuthenticated) {
      captureProductEvent('checkout-login-required', { plan, interval });
      updateProductAnalyticsContext({ userPlan: plan });
      markSignupStarted('pricing');
      onLogin();
      return;
    }

    setIsLoading(true);
    captureProductEvent('checkout-started', { plan, interval });
    updateProductAnalyticsContext({ userPlan: plan });

    try {
      const response = await fetch('/api/pricing/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          plan: plan,
          interval,
        }),
      });

      const data = await response.json();

      if (response.ok && data.url) {
        captureProductEvent('checkout-session-created', { plan, interval });
        window.location.href = data.url;
      } else {
        console.error('Failed to create checkout session:', data.error);
        captureProductEvent('checkout-failed', {
          plan,
          interval,
          status: response.status,
          errorMessage: data.error,
          failureStage: 'checkout_session',
          failureCode: `checkout_http_${response.status}`,
        });
        alert('Failed to create checkout session. Please try again.');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      captureProductEvent('checkout-failed', {
        plan,
        interval,
        errorMessage: error.message,
        failureStage: 'checkout_session',
        failureCode: 'checkout_network_error',
      });
      alert('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const plans = {
    monthly: {
      pro: { price: '$4.99', period: 'per month', yearlyPrice: '$39.99/year' },
      premium: { price: '$9.99', period: 'per month', yearlyPrice: '$79.99/year' },
      oneTime: { price: '$3.00', period: 'one time', yearlyPrice: '' },
    },
    yearly: {
      pro: { price: '$39', period: 'per year', yearlyPrice: 'Save $20.88 (4+ months free)' },
      premium: { price: '$79.99', period: 'per year', yearlyPrice: 'Save $39.89 (4+ months free)' },
      oneTime: { price: '$3.00', period: 'one time', yearlyPrice: '' },
    },
  };

  const currentPlans = plans[billingCycle];

  // Helper function to check if user has current plan
  const isCurrentPlan = plan => {
    if (!userPricing) return false;
    return userPricing.pricing_plan === plan;
  };

  // Helper function to get button text
  const getButtonText = plan => {
    if (!isAuthenticated) {
      return `Sign up for ${plan.charAt(0).toUpperCase() + plan.slice(1)}`;
    }

    if (fetchingPricing) {
      return 'Loading...';
    }

    if (isCurrentPlan(plan)) {
      return 'Current Plan';
    }

    if (isLoading) {
      return 'Loading...';
    }

    return `Choose ${plan.charAt(0).toUpperCase() + plan.slice(1)}`;
  };

  const toggleFaq = index => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const changeBillingCycle = cycle => {
    setBillingCycle(cycle);
    captureProductEvent('pricing-interval-changed', { billingCycle: cycle });
  };

  const faqData = [
    {
      question: 'How does the menu processing work?',
      answer:
        'Simply upload a photo of your menu, and our AI will extract the text, generate high-quality images for each dish, and create a beautiful digital menu with allergen information and nutritional details.',
    },
    {
      question: 'How can I cancel my subscription?',
      answer: (
        <span>
          Send an email to{' '}
          <a
            href="mailto:jordiboschbosch@hotmail.com"
            style={{ color: '#3b82f6', textDecoration: 'underline', fontWeight: '600' }}
          >
            jordiboschbosch@hotmail.com
          </a>{' '}
          with your stripe purchasing email. We will build the self-serve cancellation form ASAP.
        </span>
      ),
    },
    {
      question: 'What image formats are supported?',
      answer:
        'We support all common image formats including JPG, PNG and WebP. Be aware that sometimes "live" photos can cause problems (since they are a video under the hood).',
    },
    {
      question: 'Is there a free trial available?',
      answer:
        'Yes. You can scan menus for free and preview the first 5 dishes in every result. Upgrade to unlock every dish and all premium details.',
    },
  ];

  return (
    <div className="pricing-section">
      <div className="pricing-header">
        <div className="pricing-hero">
          <h1 className="pricing-title">Plans & Pricing</h1>
        </div>
      </div>

      <div className="billing-toggle">
        <button
          className={`interval-button ${billingCycle === 'monthly' ? 'active' : ''}`}
          onClick={() => changeBillingCycle('monthly')}
        >
          Monthly
        </button>
        <button
          className={`interval-button yearly ${billingCycle === 'yearly' ? 'active' : ''}`}
          onClick={() => changeBillingCycle('yearly')}
        >
          🔥 Yearly: get 4+ months free
        </button>
      </div>

      <div className="plans-container">
        <div className={`plan-card ${isCurrentPlan('oneTime') ? 'current-plan' : ''}`}>
          <div className="plan-header">
            <h3 className="plan-name">One Time access for one week</h3>
            <p className="plan-subtitle">"Try during one week"</p>
            <div className="plan-price">
              <span className="price">{currentPlans.oneTime.price}</span>
              <span className="period">{currentPlans.oneTime.period}</span>
            </div>
          </div>

          <div className="plan-features">
            <ul className="features-list">
              <li>50 scans (one time)</li>
              <li>Improved food models 🌮</li>
              <li>🔥 Calories included</li>
              <li>See all allergens information</li>
              <li>See all ingredients</li>
              <li>Dietary restriction filters</li>
              <li>No recurring payments</li>
              <li>Perfect for testing the service</li>
            </ul>
          </div>

          <button
            className={`plan-button primary ${isCurrentPlan('oneTime') ? 'current' : ''}`}
            onClick={() => handlePlanSelect('oneTime')}
            disabled={isLoading || fetchingPricing || isCurrentPlan('oneTime')}
          >
            {getButtonText('oneTime')}
          </button>
        </div>

        <div className={`plan-card ${isCurrentPlan('pro') ? 'current-plan' : ''}`}>
          <div className="plan-header">
            <h3 className="plan-name">Pro</h3>
            <div className="plan-price">
              <span className="price">{currentPlans.pro.price}</span>
              <span className="period">{currentPlans.pro.period}</span>
            </div>
            {billingCycle === 'monthly' && <p className="plan-yearly">or $39.99/year</p>}
            {billingCycle === 'yearly' && <p className="plan-yearly savings">{currentPlans.pro.yearlyPrice}</p>}
          </div>

          <div className="plan-features">
            <ul className="features-list">
              <li>100 scans per month</li>
              <li>Improved food models 🌮</li>
              <li>🔥 Calories included</li>
              <li>See all allergens information</li>
              <li>Detailed nutritional breakdown</li>
              <li>See all ingredients</li>
              <li>Dietary restriction filters</li>
            </ul>

            {billingCycle === 'monthly' && (
              <div className="yearly-savings">
                <button className="savings-link" onClick={() => changeBillingCycle('yearly')}>
                  Save with yearly (4+ months free) ↗
                </button>
              </div>
            )}
          </div>

          <button
            className={`plan-button primary ${isCurrentPlan('pro') ? 'current' : ''}`}
            onClick={() => handlePlanSelect('pro')}
            disabled={isLoading || fetchingPricing || isCurrentPlan('pro')}
          >
            {getButtonText('pro')}
          </button>
        </div>

        <div className={`plan-card popular ${isCurrentPlan('premium') ? 'current-plan' : ''}`}>
          <div className="popular-badge">Most popular</div>
          <div className="plan-header">
            <h3 className="plan-name">Premium</h3>
            <div className="plan-price">
              <span className="price">{currentPlans.premium.price}</span>
              <span className="period">{currentPlans.premium.period}</span>
            </div>
            {billingCycle === 'monthly' && <p className="plan-yearly">or $79.99/year</p>}
            {billingCycle === 'yearly' && <p className="plan-yearly savings">{currentPlans.premium.yearlyPrice}</p>}
          </div>

          <div className="plan-features">
            <ul className="features-list">
              <li>Unlimited scans for the time period</li>
              <li>State of the art food models 🌮 (AI)</li>
              <li>🔥 Calories included</li>
              <li>See all allergens information</li>
              <li>See all ingredients</li>
              <li>Dietary restriction filters</li>
              <li>All Plus features included</li>
              <li>Faster response time, lower latency</li>
            </ul>

            {billingCycle === 'monthly' && (
              <div className="yearly-savings">
                <button className="savings-link" onClick={() => changeBillingCycle('yearly')}>
                  Save with yearly (4+ months free) ↗
                </button>
              </div>
            )}
          </div>

          <button
            className={`plan-button primary featured ${isCurrentPlan('premium') ? 'current' : ''}`}
            onClick={() => handlePlanSelect('premium')}
            disabled={isLoading || fetchingPricing || isCurrentPlan('premium')}
          >
            {getButtonText('premium')}
          </button>
        </div>
      </div>

      <div className="faq-section">
        <div className="faq-header">
          <h2>Frequently Asked Questions</h2>
          <p className="faq-subtitle">Everything you need to know about our menu digitization service</p>
        </div>

        <div className="faq-list">
          {faqData.map((faq, index) => (
            <div key={index} className={`faq-item ${openFaqIndex === index ? 'active' : ''}`}>
              <button className="faq-question" onClick={() => toggleFaq(index)} aria-expanded={openFaqIndex === index}>
                <span className="question-text">{faq.question}</span>
                <span className={`faq-toggle ${openFaqIndex === index ? 'rotated' : ''}`}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </span>
              </button>

              <div className={`faq-answer ${openFaqIndex === index ? 'expanded' : ''}`}>
                <div className="answer-content">
                  <p>{faq.answer}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PricingSection;
